import React, {useContext, useEffect, useState} from 'react';
import {Button, Descriptions, InputNumber, Popconfirm, Select, Space, Table, Tag} from 'antd';
import dayjs from 'dayjs';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {AuthContext} from '../../../../shared/contexts/authContext';
import {getItemName} from '../../../../shared/utils/basic';
import IsLoading from '../../../../shared/components/IsLoading';
import ErrorPage from '../../../../shared/components/ErrorPage';
import {useItemKrTextData} from '../../../probability/useItemData';
import {useActivateTwinRoulettePaid, useGetTwinRouletteInfo, useSetTwinRouletteCurrency} from '../../usePlayerData';

const TABS = [
    {key: 'roulette', label: '룰렛 정보'},
    {key: 'exchange', label: '교환소 정보'},
    {key: 'edit', label: '정보 편집'},
];

// 서버 TWIN_ROULETTE_SLOT_STATE 와 동일
const SLOT_STATE = {
    0: {color: 'default', label: '진행 전'},
    1: {color: 'lightgray', label: '진행 중'},
    2: {color: 'purple', label: '보상 받음'},
    3: {color: 'orange', label: '임시 저장'},
    4: {color: 'blue', label: '완료'},
};

const GRADE = {
    1: {color: 'default', label: '일반'},
    2: {color: 'green', label: '고급'},
    3: {color: 'blue', label: '희귀'},
    4: {color: 'purple', label: '영웅'},
    5: {color: 'gold', label: '전설'},
};

const TwinRoulette = () => {
    const {playerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);
    const userKey = playerData.userKey;

    const {data, isLoading, error} = useGetTwinRouletteInfo({userKey});
    const {data: itemTextData, isLoading: isItemTextLoading, error: itemTextError} = useItemKrTextData();
    const activateMutation = useActivateTwinRoulettePaid();
    const currencyMutation = useSetTwinRouletteCurrency();

    const [tab, setTab] = useState('roulette');
    const [seq, setSeq] = useState(null);
    const [currency, setCurrency] = useState(null);

    useEffect(() => {
        if (!data) return;
        setCurrency(data.currency);
        setSeq(prev => data.rounds.some(round => round.seq === prev) ? prev : data.rounds[0]?.seq ?? null);
    }, [data]);

    if (isLoading || isItemTextLoading) return <IsLoading/>;
    if (error || itemTextError) return <ErrorPage error={error || itemTextError}/>;

    const rounds = data?.rounds || [];
    const round = rounds.find(r => r.seq === seq);

    const rewardText = (reward) => reward ? `${getItemName(itemTextData[currentUser.target], reward.id, reward.type)} x ${reward.quantity}` : '-';
    const stateTag = (state) => <Tag color={SLOT_STATE[state]?.color}>{SLOT_STATE[state]?.label}</Tag>;

    const rouletteColumns = [
        {title: '라운드', dataIndex: 'level', width: 90, render: (level) => `${level} 단계`},
        {title: '칸', dataIndex: 'slot', width: 70},
        {title: '등급', dataIndex: 'grade', width: 90, render: (grade) => <Tag color={GRADE[grade]?.color}>{GRADE[grade]?.label}</Tag>},
        {
            title: '룰렛 보상 (일반 / 유료)', key: 'rewards',
            render: (_, row) => (
                <>
                    <span style={{color: '#4a7dff'}}>{row.freeRewards.map(rewardText).join(', ') || '-'}</span>
                    {' / '}
                    <span style={{color: '#ff5a5a'}}>{row.paidRewards.map(rewardText).join(', ') || '-'}</span>
                </>
            ),
        },
        {title: '무료 보상', dataIndex: 'freeState', width: 110, render: stateTag},
        {title: '유료 보상', dataIndex: 'paidState', width: 110, render: stateTag},
    ];

    const exchangeColumns = [
        {title: '단계', dataIndex: 'step', width: 80},
        {title: '교환소 품목', dataIndex: 'reward', render: rewardText},
        {title: '교환 가격', dataIndex: 'price', render: rewardText},
        {
            title: '교환 여부', key: 'state', width: 160,
            render: (_, row) => {
                if (row.buyLimit === 0) return <Tag color="blue">{`${row.buyCount}회 교환 (무제한)`}</Tag>;
                return row.buyCount >= row.buyLimit
                    ? <Tag color="purple">교환 완료</Tag>
                    : <Tag color="lightgray">미교환</Tag>;
            },
        },
    ];

    const currencyInput = (key) => (
        <InputNumber
            min={0}
            precision={0}
            value={currency?.[key]}
            onChange={(value) => setCurrency(prev => ({...prev, [key]: value ?? 0}))}
        />
    );

    return (
        <Space direction="vertical" style={{width: '100%'}} size="middle">
            <Space wrap>
                <Select
                    value={seq}
                    onChange={setSeq}
                    style={{width: 220}}
                    placeholder="참여 회차 없음"
                    options={rounds.map(r => ({value: r.seq, label: `${r.seq} (${r.isCurrent ? '진행 중' : '종료'})`}))}
                />
                {round && (round.isPurchased ? <Tag color="purple">구매 완료</Tag> : <Tag>구매 내역 없음</Tag>)}
                <Popconfirm
                    title={`${seq} 회차의 유료 보상을 활성화하시겠습니까? 임시 저장된 보상이 우편으로 지급됩니다.`}
                    onConfirm={() => activateMutation.mutate({userKey, seq})}
                    okText="확인"
                    cancelText="취소"
                    disabled={!round || round.isPurchased}
                >
                    <Button disabled={!round || round.isPurchased} loading={activateMutation.isPending}>유료 보상 활성화</Button>
                </Popconfirm>
            </Space>

            <Space>
                {TABS.map(({key, label}) => (
                    <Button key={key} type={tab === key ? 'primary' : 'default'} onClick={() => setTab(key)}>{label}</Button>
                ))}
            </Space>

            {tab === 'roulette' && (
                <Table size="small" columns={rouletteColumns} dataSource={round?.slots || []} pagination={false} scroll={{y: 600}}/>
            )}

            {tab === 'exchange' && (
                <Table size="small" columns={exchangeColumns} dataSource={round?.exchange || []} pagination={false}
                       locale={round && !round.isCurrent ? {emptyText: '교환소 정보는 진행 중인 회차만 확인할 수 있습니다.'} : undefined}/>
            )}

            {tab === 'edit' && (
                <>
                    <Descriptions bordered size="small" column={2} title="기본 정보">
                        <Descriptions.Item label="이벤트 시퀀스">{round?.seq ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="오픈 레벨">{round?.openLevel ?? '-'}</Descriptions.Item>
                        <Descriptions.Item label="룰렛 코인">{currencyInput('rouletteCoin')}</Descriptions.Item>
                        <Descriptions.Item label="교환소 코인">{currencyInput('exchangeCoin')}</Descriptions.Item>
                        <Descriptions.Item label="코스튬 교환 코인">{currencyInput('costumeCoin')}</Descriptions.Item>
                        <Descriptions.Item label="갱신 일시">
                            {round?.updatedAt ? dayjs(round.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                        </Descriptions.Item>
                    </Descriptions>
                    <Popconfirm
                        title="재화 보유량을 저장하시겠습니까?"
                        onConfirm={() => currencyMutation.mutate({userKey, ...currency})}
                        okText="확인"
                        cancelText="취소"
                    >
                        <Button type="primary" loading={currencyMutation.isPending}>저장</Button>
                    </Popconfirm>
                </>
            )}
        </Space>
    );
};

export default TwinRoulette;
